import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const HERO_IMG = "https://images.unsplash.com/photo-1741356474357-72188f76e801?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdvbGQlMjBmbHVpZCUyMG9uJTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg3NTg0NjQzfDA&ixlib=rb-4.1.0&q=85";

const EASE = [0.16, 1, 0.3, 1];

const LINES = [
  { text: "Every great idea", gold: false },
  { text: "deserves a", gold: false },
  { text: "global defence.", gold: true },
];

export default function Hero({ start = true }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const state = start ? "show" : "hidden";
  const fadeUp = (delay, y = 24) => ({
    hidden: { opacity: 0, y },
    show: { opacity: 1, y: 0, transition: { duration: 0.9, delay, ease: EASE } },
  });

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img src={HERO_IMG} alt="" className="w-full h-[120%] object-cover opacity-30" />
        <div className="absolute inset-0 bg-navy-950/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-transparent to-navy-950/60" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="max-w-7xl mx-auto px-6 sm:px-8 pt-32 pb-24 w-full">
        <motion.p
          variants={fadeUp(0.15, 20)}
          initial="hidden"
          animate={state}
          className="text-xs uppercase tracking-[0.35em] text-gold font-semibold mb-10"
        >
          IPR Consultants · Patents | Trademarks | Designs | Copyrights
        </motion.p>

        <h1 className="font-serif font-light tracking-tight leading-[0.95] text-5xl sm:text-7xl lg:text-[6.5rem]">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-2">
              <motion.span
                variants={{
                  hidden: { y: "110%" },
                  show: { y: 0, transition: { duration: 1.2, delay: 0.3 + i * 0.15, ease: EASE } },
                }}
                initial="hidden"
                animate={state}
                className={`block ${line.gold ? "italic text-gold" : "text-white"}`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          variants={fadeUp(0.9)}
          initial="hidden"
          animate={state}
          className="mt-10 max-w-xl text-mist text-base sm:text-lg leading-relaxed"
        >
          ClaimSpace LegalTech is a full-service IPR firm — patents, trademarks, designs,
          copyrights and patent analytics — guiding innovators, startups, law firms and
          corporates from first idea to granted right, in India and worldwide.
        </motion.p>

        <motion.div
          variants={fadeUp(1.05)}
          initial="hidden"
          animate={state}
          className="mt-12 flex flex-wrap items-center gap-5"
        >
          <a href="#contact" data-testid="hero-quote-button"
            className="group inline-flex items-center gap-3 px-8 py-4 bg-gold text-navy-950 font-semibold text-sm tracking-wide hover:bg-gold-light transition-colors duration-300">
            Request a Quote
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <Link to="/portal/login" data-testid="hero-portal-link"
            className="inline-flex items-center gap-3 px-8 py-4 border border-white/20 text-white text-sm tracking-wide hover:border-gold hover:text-gold transition-colors duration-300">
            Client Portal
          </Link>
        </motion.div>

        <motion.div
          variants={fadeUp(1.3, 0)}
          initial="hidden"
          animate={state}
          className="mt-20 grid grid-cols-3 max-w-lg gap-8 border-t border-white/10 pt-8"
        >
          {[["05", "Practice areas"], ["08+", "Technology domains"], ["360°", "Idea to granted right"]].map(([n, l]) => (
            <div key={l}>
              <p className="font-serif text-3xl sm:text-4xl text-gold">{n}</p>
              <p className="text-xs uppercase tracking-[0.2em] text-mist mt-2">{l}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { delay: 1.8, duration: 1 } } }}
        initial="hidden"
        animate={state}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-mist"
      >
        <ArrowDown size={20} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
